import { APP_NAME } from '@repo/shared_utils/constants'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, FileText, Mail, Scale, Shield, Users } from 'lucide-react'
import LandingPageWrapper from '../components/landing/landing-page-wrapper'

export const Route = createFileRoute('/terms')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <LandingPageWrapper>
      <div className="max-w-4xl mx-auto space-y-12 py-12">
        {/* Header */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-full">
              <FileText className="h-12 w-12 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Terms of Service
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Please read these terms carefully before using {APP_NAME}. By using our service, you
            agree to these terms.
          </p>
          <p className="text-sm text-muted-foreground">Last updated: Sept 24, 2025</p>
        </div>

        {/* Acceptance of Terms */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Scale className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold">Acceptance of Terms</h2>
          </div>

          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              By accessing or using {APP_NAME} ("Service"), you agree to be bound by these Terms of
              Service ("Terms"). If you disagree with any part of these terms, then you may not
              access the Service.
            </p>
            <p>
              These Terms apply to all visitors, users, and others who access or use the Service,
              including businesses, employees, and administrators who utilize our payroll
              management platform.
            </p>
          </div>
        </div>

        {/* Service Description */}
        <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <Users className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold">Service Description</h2>
          </div>

          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              {APP_NAME} is a cloud-based payroll management platform that helps businesses in the
              Philippines process payroll, manage employee information, track time and attendance,
              and ensure compliance with local labor laws and tax regulations.
            </p>

            <div>
              <h3 className="text-xl font-semibold mb-3">Our Services Include:</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Payroll processing and employee payment management
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Tax calculation and compliance reporting (BIR, SSS, PhilHealth, Pag-IBIG)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Employee information management and HR tools
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Time tracking and attendance monitoring
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Document generation and record keeping
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* User Responsibilities */}
        <div className="bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-amber-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <Shield className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold">User Responsibilities</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Account Security</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Maintain the confidentiality of your account credentials
                </li>

                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Notify us immediately of any unauthorized access or security breach
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Log out of your account when using shared or public devices
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Data Accuracy</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Provide accurate and up-to-date employee information
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Ensure all payroll data and tax information is correct
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Review and verify all generated reports before submission
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Update employee records promptly when changes occur
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Compliance</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Comply with all applicable Philippine labor laws and regulations
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Obtain necessary employee consents for data processing
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Use the Service only for legitimate business purposes
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Service Availability */}
        <div className="bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-red-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold">Service Availability & Limitations</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Service Availability</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  We strive for 99.9% uptime but cannot guarantee uninterrupted service
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Scheduled maintenance will be announced in advance
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Emergency maintenance may occur without prior notice
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Liability Limitations</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  You are responsible for verifying all calculations and reports
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  We provide tools but you remain responsible for legal compliance
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Our liability is limited to the amount paid for the Service
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  We are not liable for indirect damages
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Termination */}
        <div className="bg-gradient-to-br from-gray-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-slate-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <h2 className="text-3xl font-bold mb-6">Account Termination</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">By You</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  You may cancel your subscription at any time through your account settings
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  Service continues until the end of your current billing period
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  You can export your data before account closure
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">By Us</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  We may terminate accounts for violation of these Terms
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  30 days notice will be provided for non-compliance issues
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                  Immediate termination may occur for serious violations
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <h2 className="text-3xl font-bold mb-6">Governing Law</h2>

          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              These Terms are governed by and construed in accordance with the laws of the Republic
              of the Philippines. Any disputes arising from or relating to these Terms or the use
              of our Service shall be resolved through the appropriate courts in the Philippines.
            </p>
            <p>
              We comply with all applicable Philippine laws including but not limited to the Data
              Privacy Act of 2012, Labor Code of the Philippines, and relevant tax regulations from
              the Bureau of Internal Revenue.
            </p>
          </div>
        </div> */}

        {/* Changes to Terms */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20 border border-teal-200 dark:border-teal-800 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4">Changes to Terms</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We reserve the right to modify these Terms at any time. When we make changes, we
              will:
            </p>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></span>
                Update the "Last updated" date at the top of this page
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></span>
                Notify you if the changes are significant
              </li>
            </ul>
            <p>
              Your continued use of the Service after changes are made constitutes acceptance of
              the new Terms.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20 border border-violet-200 dark:border-violet-800 rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
              <Mail className="h-8 w-8 text-violet-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-4">Questions About These Terms?</h2>
          <p className="text-muted-foreground mb-6 text-lg">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2">
            <p className="text-lg">
              <strong>Support:</strong>{' '}
              <a
                href="mailto:cubic.payroll.101@gmail.com"
                className="text-violet-600 hover:text-violet-700 font-medium"
              >
                cubic.payroll.101@gmail.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </LandingPageWrapper>
  )
}
